import React, { useMemo } from 'react';
import { Form } from 'react-final-form';
import { useMutation, useQuery } from '@apollo/client';
import { Icon } from '@tt/ui';
import cx from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';

import updateUploadedDocumentGQL from '../../graphql/mutations/uploaded_documents/updateUploadedDocument.graphql';
import documentsFoldersQuery from '../../graphql/queries/all_documents/documentFolders.graphql';
import { useUploadDocumentFlow } from '../../hooks/useUploadDocumentFlow';
import { removeFromApolloCache } from '../../services/utilities';
import Button from '../Button';
import IconButton from '../IconButton';
import Loader from '../Loader';
import Modal from '../Modal';
import { H2 } from '../Typography';

import { UPLOAD_DOCUMENT_STEPS } from './steps/constants';
import RenderStep from './steps/RenderStep';
import { UploadDocumentContext } from './context';

import styles from './UploadDocumentModal.module.scss';

const UploadDocumentModal = ({ className, open = false, onCloseModal }) => {
  const { data, loading: loadingFolders } = useQuery(documentsFoldersQuery);
  const folderData = data?.documentFolders || {};

  const [updateUploadedDocument, { loading: uploadingDocument }] = useMutation(
    updateUploadedDocumentGQL,
    {
      update: (cache) => removeFromApolloCache(['allDocuments'], cache),
    },
  );

  const {
    step,
    setStep,
    nextStep,
    handleFileDrop,
    setFileAttached,
    fileAttached,
    setFormData,
    formData,
    fileUploading,
    uploadDocument,
    resetFlow,
    onClose,
    setFileUploading,
  } = useUploadDocumentFlow(updateUploadedDocument, onCloseModal);

  const onEdit = () => setStep(UPLOAD_DOCUMENT_STEPS.SELECT_DESTINATION);

  const onSubmit = async (data) => {
    setFileUploading(true);
    try {
      if (step === UPLOAD_DOCUMENT_STEPS.SET_DOCUMENT_DETAILS) {
        await uploadDocument({
          updateData: {
            fileName: data.documentTitle,
            locationType: data.destination.location_type,
            locationId: data.destination.location_id,
            isShared: data.is_shared,
            reportedType: data.reported_type,
            reportedTypeOther: data.reported_type_other || undefined,
            expirationDate: data.expiration_date
              ? moment(data.expiration_date)
              : null,
          },
        });
      } else {
        setFormData((prev) => ({
          ...prev,
          ...data,
          documentTitle:
            step === UPLOAD_DOCUMENT_STEPS.SET_DOCUMENT_NAME
              ? data.name
              : prev.name,
        }));
        nextStep();
      }
    } finally {
      setFileUploading(false);
    }
  };

  const contextValue = useMemo(
    () => ({
      step,
      setStep,
      nextStep,
      handleFileDrop,
      setFileAttached,
      fileAttached,
      setFormData,
      formData,
      onEdit,
      fileUploading,
      folderData,
    }),
    [step, fileAttached, formData, fileUploading, folderData],
  );

  return (
    <Modal
      className={cx(styles.container, className)}
      open={open}
      autoFocus={false}
      disableOutsideClick
      onClose={onClose}
      size="small"
      showCloseIcon
    >
      <UploadDocumentContext.Provider value={contextValue}>
        {uploadingDocument ? (
          <Loader />
        ) : (
          <>
            <H2>Upload your document</H2>

            {fileAttached && (
              <div className={styles.uploadedState}>
                <div className={styles.fileItem}>
                  <div className={styles.fileName}>
                    <Icon name="document" color="close-lightbg" />
                    <span className={styles.title}>
                      {formData.documentTitle}
                    </span>
                  </div>
                  <IconButton
                    icon="delete"
                    iconProps={{ color: 'error' }}
                    className={styles.remove}
                    onClick={resetFlow}
                  />
                </div>
              </div>
            )}

            <Form onSubmit={onSubmit} initialValues={formData}>
              {({ handleSubmit, submitting, values, form }) => (
                <form onSubmit={handleSubmit}>
                  <RenderStep values={values} form={form} />
                  {fileAttached && (
                    <div className={styles.buttonWrapper}>
                      <Button
                        type="submit"
                        disabled-text="Continue"
                        disabled={
                          (step === UPLOAD_DOCUMENT_STEPS.SELECT_DESTINATION &&
                            !values.destination) ||
                          loadingFolders
                        }
                        className={styles.submitButton}
                        loading={submitting || loadingFolders}
                      >
                        Continue
                      </Button>
                    </div>
                  )}
                </form>
              )}
            </Form>
          </>
        )}
      </UploadDocumentContext.Provider>
    </Modal>
  );
};

UploadDocumentModal.propTypes = {
  className: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onCloseModal: PropTypes.func,
};

export default UploadDocumentModal;
